import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import classes from './editUserDialog.module.scss';
import { ReactComponent as FillClose } from '../../../assets/images/fillClose.svg';
import { ReactComponent as Close } from '../../../assets/images/close.svg';
import { ReactComponent as Point } from '../../../assets/images/point.svg';
import { FormControl } from '../../ui/FormControl';
import { ReactComponent as SubscribeActive } from '../../../assets/images/SubscribeActive.svg';
import { ReactComponent as Subscribe } from '../../../assets/images/Subscribe.svg';
import { AutoComplete } from '../../ui/AutoComplete';
import {
  useRole,
  useUsers,
  useUpdateUser,
  useSubdivivsion,
} from '../../../hooks/entityHooks';
import { RoleResponse } from '../../../types/role';
import { UserUpdate } from '../../../types/user';
import { Multiselect } from '../../ui/Multiselect';
import { SUBDIVISION_HEAD_ID } from '../../../types/utils/constants';
import { Subdivision } from '../../../types/subdivision';

type FormikValue = {
  email: string;
  surname: string;
  name: string;
  roleName: RoleResponse;
  subdivisions: Array<Subdivision>;
};

type Role = {
  id: number;
  name: string;
};

type Props = {
  id: number;
  open: boolean;
  handleClose(): void;
  handleChange(): void;
};

export const EditUserDialog = ({
  id,
  open,
  handleClose,
  handleChange,
}: Props) => {
  const [focus, setFocus] = useState(false);
  const [subscribe, setSubscribe] = useState(false);
  const [areSubdivisionsDisabled, setAreSubdivisionsDisabled] = useState(true);
  const [subdivisions, setSubdivisions] = useState<Subdivision[]>([]);

  const { getAllSubdivisions } = useSubdivivsion();
  const { entityList: roles, getEntityList: getRoles } = useRole();
  const { getEntity: getUser, entity: user, getEntityList: list } = useUsers();
  const { updateEntity: updateUser } = useUpdateUser();

  useEffect(() => {
    if (id) {
      const result = getUser(id);
      result.then(() => {
        getRoles();
      });
    }
  }, [id]);

  useEffect(() => {
    getAllSubdivisions().then((res) => setSubdivisions(res));
  }, []);

  const useStyles = makeStyles({
    root: {
      background: '#FF7221',
      borderRadius: 0,
      color: 'white',
      fontWeight: 550,
      textTransform: 'none',
      '&:hover, &:focus': {
        background: '#DE6109',
      },
      fontFamily: 'HelveticaNeueCyr',
      // boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
      height: 50,
      padding: '0 30px',
    },
  });
  const {
    handleSubmit,
    handleBlur,
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
  } = useFormik<FormikValue>({
    initialValues: {
      email: '',
      name: '',
      roleName: {
        id: 0,
        name: '',
      },
      subdivisions: [],
      surname: '',
    },
    onSubmit: () => {
      const user: UserUpdate = {
        email: values.email,
        id,
        name: values.name,
        role: values.roleName.id,
        subscription: subscribe,
        surname: values.surname,
        subdivisions: values.subdivisions,
      };
      const result = updateUser(user);
      result.then(() => {
        handleClose();
        handleChange();
      });
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().required('Обязательное поле'),
      name: Yup.string().required('Обязательное поле'),
      surname: Yup.string().required('Обязательное поле'),
    }),
  });

  useEffect(() => {
    if (user) {
      const {
        email,
        name,
        roleName,
        surname,
        subscription,
        subdivisions,
      } = user;
      setSubscribe(subscription);
      const newRole = roles.find((el) => el.name === roleName);
      setValues({
        surname,
        email,
        name,
        roleName: newRole,
        subdivisions,
      });
    }
  }, [user, roles]);

  const style = useStyles();

  const onRoleChange = (value: Role | any) => {
    setAreSubdivisionsDisabled(value.id !== SUBDIVISION_HEAD_ID);
    setFieldValue('roleName', value);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <form className={classes.form} onSubmit={handleSubmit}>
          <DialogTitle>
            <div className={classes.form__dialogTitle}>
              <span>Редактирование пользователя</span>
              <div
                className={classes.close}
                onMouseOver={(e) => setFocus(true)}
                onMouseOut={(e) => setFocus(false)}
                onClick={(e) => handleClose()}
              >
                {focus ? <FillClose /> : <Close />}
              </div>
            </div>
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            <div>
              <div className={classes.title}>
                <p>Email</p>
                <Point className={classes.title__point} />
              </div>

              <div>
                <FormControl
                  placeholder="Email"
                  type="text"
                  name="email"
                  value={values.email}
                  onChange={(e) => setFieldValue('email', e.target.value)}
                  error={Boolean(errors?.email && touched?.email)}
                  errorMessage={errors?.email}
                  onBlur={handleBlur}
                  // onFocus={setFieldTouched('email', false)}
                />
              </div>
            </div>

            <div>
              <div className={classes.title}>
                <p>Фамилия</p>
                <Point className={classes.title__point} />
              </div>

              <div>
                <FormControl
                  placeholder="Фамилия"
                  type="text"
                  name="surname"
                  value={values.surname}
                  onChange={(e) => setFieldValue('surname', e.target.value)}
                  error={Boolean(errors?.surname && touched?.surname)}
                  errorMessage={errors?.surname}
                  onBlur={handleBlur}
                />
              </div>
            </div>

            <div>
              <div className={classes.title}>
                <p>Имя</p>
                <Point className={classes.title__point} />
              </div>

              <div>
                <FormControl
                  placeholder="Имя"
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue('name', e.target.value)}
                  error={Boolean(errors?.name && touched?.name)}
                  errorMessage={errors?.name}
                  onBlur={handleBlur}
                />
              </div>
            </div>

            <div className={classes.form__autoSelect}>
              <div className={classes.title}>
                <p>Роль</p>
                <Point className={classes.title__point} />
              </div>
              <AutoComplete
                options={roles}
                getOptionLabel={(option) =>
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  option.name ? option.name : 'Роль'
                }
                value={values.roleName}
                onChange={(event, value) => onRoleChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Роль"
                    variant="outlined"
                    name="roleName"
                    error={Boolean(errors?.roleName && touched?.roleName)}
                  />
                )}
              />
              {errors?.roleName && touched?.roleName ? (
                <p className={classes.form__autoSelect__p}>
                  {errors?.roleName}
                </p>
              ) : null}
            </div>

            <div>
              <div className={classes.title}>
                <p>Подразделение</p>
                <Point className={classes.title__point} />
              </div>

              <Multiselect
                subdivisions={subdivisions}
                defaultSelectedSubdivisions={values.subdivisions}
                disabled={areSubdivisionsDisabled}
                onSelect={(value) => setFieldValue('subdivisions', value)}
              />
            </div>

            <div className={classes.form__suscribe}>
              <div onClick={(e) => setSubscribe(!subscribe)}>
                {subscribe ? (
                  <SubscribeActive className={classes.form__suscribe__logo} />
                ) : (
                  <Subscribe className={classes.form__suscribe__logo} />
                )}
              </div>

              <div>Подписка</div>
            </div>
          </DialogContent>

          <div className={classes.form__row__button}>
            <Button
              className={style.root}
              // onClick={handleClose}
              type="submit"
              color="primary"
            >
              Редактировать пользователя
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
};
